/**
 * Formatting PDF Generator
 * 
 * Generates professionally formatted PDFs according to the Fernando Hidalgo Law Firm
 * specifications for wider audience consumption.
 * 
 * PDF Specifications:
 * - US Letter (8.5" × 11" / 612 × 792 points)
 * - Margins: 60pt left/right, 72pt top/bottom
 * - Font: Helvetica family throughout
 * - Title Page with executive summary
 * - Numbered Parts structure
 * - References Page
 * - Page numbers and copyright on every page
 */

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import {
  loadUnicodeFontFamily,
  loadStandardFonts,
  detectLanguage,
  requiresUnicodeFont,
} from './pdfFonts';
import type { PDFFont } from './pdfFonts';
import {
  normalizeContent,
  sanitizeForPdf,
  wrapText,
  safeDrawText,
  extractAndPreserveReferences,
  convertReferencesToPlaintext,
} from './pdfTextUtils';

// ============================================================================
// Types
// ============================================================================

/**
 * Translation overrides for creating translated PDF documents.
 * When provided, these values replace the default English text.
 */
export interface FormattedPdfTranslationOverrides {
  /** Translated document title */
  title?: string;
  /** Translated date string (e.g., "15 de enero de 2024") */
  date?: string;
  /** Firm/organization name (typically kept as-is) */
  organizationName?: string;
  /** Translated full copyright notice */
  copyrightNotice?: string;
  /** Translated short copyright for footers */
  copyrightShort?: string;
  /** Translated page number format (e.g., "Página {current}") */
  pageNumberFormat?: string;
}

export interface BuildFormattedPdfParams {
  /** The formatted report content from Claude */
  content: string;
  /** Original research query/topic for the title */
  title: string;
  /** Date of report creation */
  createdAt?: Date;
  /** Optional translation overrides for creating translated PDFs */
  translations?: FormattedPdfTranslationOverrides;
  /**
   * Emit the generated cover page (title / date / copyright).
   *
   * Defaults to true — a research report we author ourselves wants one. Set
   * false when the content IS a document the user supplied: a translated
   * 45-page financial report already carries its own title page and running
   * header, so prepending ours produced the "broken first page" in the
   * 2026-08-13 formatting feedback, and shifted every page by one.
   */
  includeCoverPage?: boolean;
  /**
   * Figures pulled from the SOURCE document, tagged with the page they sat on.
   * Drawn when the deliverable reaches that source page, so a translated
   * report keeps its charts (2026-08-13 formatting feedback: 35 figures in,
   * zero out — this builder had no image support at all).
   */
  /**
   * Called as pages are laid down.
   *
   * WHY (2026-08-21 sweep): #989 gave the build a LABEL ("building your PDF
   * and Word file") but nothing that moves. A 200-page deliverable with 120
   * figures takes minutes, so the label was a frozen 100% with better wording
   * — the third instance of this defect taught us a static label is not
   * progress.
   */
  onProgress?: (info: { pages: number }) => void;
  sourceImages?: Array<{
    pageNumber: number;
    base64: string;
    widthPx: number;
    heightPx: number;
    /** Fraction of the source page's text above the figure; see SourcePageImage. */
    afterCharRatio?: number;
  }>;
}

// ============================================================================
// Constants - PDF Specifications
// ============================================================================

// Page dimensions (US Letter)
const PAGE_WIDTH = 612; // 8.5 inches in points
const PAGE_HEIGHT = 792; // 11 inches in points

// Margins
const MARGIN_LEFT = 60;
const MARGIN_RIGHT = 60;
const MARGIN_TOP = 72;
const MARGIN_BOTTOM = 72;

// Calculated content area
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT;

// Font sizes (as specified)
const FONT_SIZE_TITLE = 18;
const FONT_SIZE_SUBTITLE = 14;
const FONT_SIZE_PART_HEADER = 14;
const FONT_SIZE_SECTION_HEADER = 12;
const FONT_SIZE_BODY = 10;
const FONT_SIZE_FOOTER = 8;
const FONT_SIZE_PAGE_NUM = 10;

// Line heights and spacing
const LINE_HEIGHT_BODY = 14; // 1.15× line height for 10pt text (~14pt)
const PARAGRAPH_SPACING = 12;
const BEFORE_SECTION_HEADER = 18;
const AFTER_SECTION_HEADER = 6;
const BULLET_SPACING = 6;

// Indents
const BULLET_INDENT = 24;
const CALLOUT_INDENT = 12;

// Footer position
const FOOTER_Y = 36;
const PAGE_NUM_Y = 48;

// Organization info
const ORGANIZATION = 'The Law Offices of Fernando Hidalgo, Inc.';

// ============================================================================
// Block Parsing
// ============================================================================

type BlockKind = 'title' | 'part' | 'section' | 'subsection' | 'bullet' | 'numbered' | 'callout' | 'paragraph' | 'table' | 'pagebreak';

/**
 * A page marker emitted by our own PDF text extractor
 * (`extractTextFromPdfClientSide` writes "--- Page N ---" between pages).
 *
 * INCIDENT-20260813 (formatting fidelity): these markers were translated along
 * with the document and then rendered as ORDINARY BODY TEXT, so a 45-page
 * source came back with "--- Page 1 ---" sitting at the start of paragraphs,
 * while the deliverable itself reflowed to 25 pages of its own. Treating the
 * marker as a real page break instead both removes the stray text and keeps
 * the output's pagination aligned with the source.
 *
 * Tolerant of translation: the model may render "Page" in the target language
 * or vary the dashes, so match the shape rather than the exact string.
 */
export const SOURCE_PAGE_MARKER = /^\s*-{2,}\s*[\p{L}]+\s+(\d+)\s*-{2,}\s*$/u;

/**
 * The same marker found ANYWHERE, not just alone on its line.
 *
 * Round 2 (2026-08-13): requiring a standalone line was correct but not
 * sufficient. The marker travels through the translator, and the model
 * routinely returns it welded to the page's first sentence
 * ("--- Page 3 --- ANNUAL FINANCIAL REPORT 2025 ..."). SOURCE_PAGE_MARKER then
 * failed to match, every page break was lost, and the marker was rendered as
 * prose at the head of a paragraph — the exact symptom the first fix was meant
 * to remove.
 *
 * Widening SOURCE_PAGE_MARKER itself would let it swallow real content, so the
 * text is normalised FIRST instead: each marker is lifted onto its own line,
 * after which the strict rule matches. The surrounding dashes are what make
 * this safe — "Page 1 of 45" and "See Page 12" carry none and are untouched.
 */
const INLINE_SOURCE_PAGE_MARKER = /-{2,}\s*[\p{L}]+\s+(\d+)\s*-{2,}/gu;

/**
 * Lift every source page marker onto its own line so the block parser sees it.
 * Canonicalises to "--- Page N ---": only the number survives into the page
 * break, and a canonical form keeps the PDF and DOCX builders in lockstep.
 */
export function normalizeSourcePageMarkers(content: string): string {
  return (content || '').replace(
    INLINE_SOURCE_PAGE_MARKER,
    (_match, pageNumber) => `\n--- Page ${pageNumber} ---\n`,
  );
}

interface Block {
  kind: BlockKind;
  text: string;
  isBold?: boolean;
  isItalic?: boolean;
  isLink?: boolean;
  url?: string;
  /** Table data (only for kind === 'table') */
  tableRows?: string[][];
  /** Page number this break came from in the SOURCE (kind === 'pagebreak'). */
  sourcePageNumber?: number;
}

/**
 * Parse a markdown table row (line starting with |) into cells.
 */
function parseTableRow(line: string): string[] {
  return line
    .split('|')
    .map(cell => cell.trim())
    .filter((cell, idx, arr) => {
      // Remove empty first/last cells created by | at the start/end of line
      if (idx === 0 && cell === '') return false;
      if (idx === arr.length - 1 && cell === '') return false;
      return true;
    })
    .map(cell => cell.trim());
}

/**
 * Check if a line is a markdown table separator (contains only -, :, |, and spaces).
 */
function isTableSeparator(line: string): boolean {
  return /^[\s|:-]+$/.test(line) && line.includes('-');
}

function parseBlocks(content: string): Block[] {
  const blocks: Block[] = [];
  const lines = (content || '').split('\n');
  let numberedCounter = 0;
  let tableBuffer: string[] = [];

  // Helper to flush accumulated table rows into a table block
  const flushTable = () => {
    if (tableBuffer.length === 0) return;

    // Parse table rows
    const hasSeparator = tableBuffer.length > 1 && isTableSeparator(tableBuffer[1]);
    const dataRows = hasSeparator ? [tableBuffer[0], ...tableBuffer.slice(2)] : tableBuffer;
    const tableRows = dataRows
      .filter(row => !isTableSeparator(row))
      .map(parseTableRow)
      .filter(row => row.length > 0);

    if (tableRows.length > 0) {
      blocks.push({
        kind: 'table',
        text: '',
        tableRows,
      });
    }
    tableBuffer = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushTable();
      continue;
    }

    // Detect markdown table rows (lines starting with |)
    if (line.startsWith('|')) {
      tableBuffer.push(line);
      continue;
    }

    // Not a table line - flush any pending table
    flushTable();

    // Source page marker -> real page break, never body text (see
    // SOURCE_PAGE_MARKER). Checked before every other block rule so the marker
    // can never be mistaken for a heading or a paragraph.
    const pageMarker = line.match(SOURCE_PAGE_MARKER);
    if (pageMarker) {
      blocks.push({ kind: 'pagebreak', text: '', sourcePageNumber: Number(pageMarker[1]) });
      continue;
    }

    // Detect Part headers: "Part X:" or "**Part X:**" at start
    const partMatch = line.match(/^\*?\*?Part\s+(\d+)[:.]?\*?\*?\s*(.*)$/i);
    if (partMatch) {
      numberedCounter = 0;
      const partTitle = partMatch[2] ? `Part ${partMatch[1]}: ${partMatch[2].trim()}` : `Part ${partMatch[1]}`;
      blocks.push({ kind: 'part', text: partTitle });
      continue;
    }

    // Detect title (# at start, single hash)
    if (line.startsWith('# ') && !line.startsWith('## ')) {
      numberedCounter = 0;
      blocks.push({ kind: 'title', text: line.slice(2).trim() });
      continue;
    }

    // Detect section headers (## at start)
    if (line.startsWith('## ')) {
      numberedCounter = 0;
      blocks.push({ kind: 'section', text: line.slice(3).trim() });
      continue;
    }

    // Detect subsection headers (### at start)
    if (line.startsWith('### ')) {
      numberedCounter = 0;
      blocks.push({ kind: 'subsection', text: line.slice(4).trim() });
      continue;
    }

    // Detect callout boxes (Important:, Critical:, Example:, Note:, Warning:)
    const calloutMatch = line.match(/^\*?\*?(Important|Critical|Example|Note|Warning):\*?\*?\s*(.*)$/i);
    if (calloutMatch) {
      numberedCounter = 0;
      const label = calloutMatch[1];
      const calloutText = calloutMatch[2] || '';
      blocks.push({ kind: 'callout', text: `${label}: ${calloutText}`.trim() });
      continue;
    }

    // Detect bold labels like **Included:** or **NOT Included:** (for Important Notice section)
    const boldLabelMatch = line.match(/^\*\*(Included|NOT Included):?\*\*\s*$/i);
    if (boldLabelMatch) {
      numberedCounter = 0;
      blocks.push({ kind: 'subsection', text: boldLabelMatch[1] + ':' });
      continue;
    }

    // Detect bullet points (- or * at start)
    if (/^[-*]\s+/.test(line)) {
      numberedCounter = 0;
      blocks.push({ kind: 'bullet', text: line.replace(/^[-*]\s+/, '').trim() });
      continue;
    }

    // Detect numbered lists
    const numMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (numMatch) {
      numberedCounter = parseInt(numMatch[1], 10);
      blocks.push({ kind: 'numbered', text: `${numberedCounter}. ${numMatch[2].trim()}` });
      continue;
    }

    // Default: paragraph
    numberedCounter = 0;
    blocks.push({ kind: 'paragraph', text: line });
  }

  // Flush any remaining table
  flushTable();

  return blocks;
}

// ============================================================================
// Important Notice Extraction
// ============================================================================

/**
 * Result of extracting the Important Notice section from parsed blocks.
 * The notice blocks are rendered on the cover page, remaining blocks on content pages.
 */
interface ImportantNoticeExtraction {
  noticeBlocks: Block[];
  remainingBlocks: Block[];
}

/**
 * Extract Important Notice section from parsed blocks.
 * Detects "## Important Notice" section and collects all blocks until the next major section.
 * This allows the Important Notice to be rendered on the cover page while the rest
 * of the content starts on Page 2.
 *
 * @param blocks - All parsed content blocks
 * @returns Object with noticeBlocks (for cover page) and remainingBlocks (for content pages)
 */
function extractImportantNotice(blocks: Block[]): ImportantNoticeExtraction {
  const noticeBlocks: Block[] = [];
  const remainingBlocks: Block[] = [];
  let inNoticeSection = false;
  let noticeEnded = false;

  for (const block of blocks) {
    // Detect start of Important Notice section
    if (!noticeEnded && block.kind === 'section' &&
        block.text.toLowerCase().includes('important notice')) {
      inNoticeSection = true;
      noticeBlocks.push(block);
      continue;
    }

    // If in notice section, collect blocks until we hit a major section break
    if (inNoticeSection && !noticeEnded) {
      // End notice section when we hit:
      // - A title (# heading)
      // - A part header
      // - A non-notice section header (## that isn't "Included" or "NOT Included")
      const isEndMarker =
        block.kind === 'title' ||
        block.kind === 'part' ||
        (block.kind === 'section' &&
         !block.text.toLowerCase().includes('included') &&
         !block.text.toLowerCase().includes('notice'));

      if (isEndMarker) {
        inNoticeSection = false;
        noticeEnded = true;
        remainingBlocks.push(block);
      } else {
        noticeBlocks.push(block);
      }
    } else {
      remainingBlocks.push(block);
    }
  }

  return { noticeBlocks, remainingBlocks };
}

// ============================================================================
// PDF Builder
// ============================================================================

/**
 * An organisation name in a position that supplies its own full stop.
 *
 * "The Law Offices of Fernando Hidalgo, Inc." already ends in one, so
 * `${organizationName}.` printed "Inc.." in the footer of every page of the
 * 200-page deliverable (2026-08-20).
 */
export function organizationNameForSentence(name: string): string {
  return (name || '').trim().replace(/\.+$/, '');
}

/**
 * Collapse a doubled full stop, leaving an ellipsis alone.
 *
 * WHY (2026-08-21): organizationNameForSentence only guards the notice WE
 * compose. The translator supplies its own translated copyright line, which
 * takes precedence — and it came back as "The Law Offices of Fernando Hidalgo,
 * Inc.. All rights reserved.", printed on all 200 pages of the re-run. Guard
 * the value that actually reaches the page, whoever wrote it.
 */
export function tidyDoubledPeriods(text: string): string {
  return (text || '').replace(/([^.])\.\.(?!\.)/g, '$1.');
}

export async function buildFormattedPdf(params: BuildFormattedPdfParams): Promise<File> {
  const createdAt = params.createdAt || new Date();
  const year = createdAt.getFullYear();
  const translations = params.translations || {};

  // Use translated values if provided, otherwise use defaults
  const organizationName = translations.organizationName || ORGANIZATION;
  const dateStr = translations.date || createdAt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const orgForSentence = organizationNameForSentence(organizationName);
  const copyrightNotice = tidyDoubledPeriods(translations.copyrightNotice || `(c) ${year} ${orgForSentence}. Generated by Prest0n. Facilitated by ${orgForSentence}. All rights reserved.`);
  const copyrightShort = tidyDoubledPeriods(translations.copyrightShort || `(c) ${year} ${orgForSentence}. All rights reserved.`);
  const pageNumberFormat = translations.pageNumberFormat || 'Page {current}';

  const pdfDoc = await PDFDocument.create();

  // Detect if content requires Unicode fonts (for Korean/CJK characters)
  const contentToCheck = [
    params.content,
    params.title,
    translations.title,
    translations.date,
    translations.copyrightNotice,
    translations.copyrightShort,
    translations.pageNumberFormat,
  ].filter(Boolean).join(' ');

  const needsUnicode = requiresUnicodeFont(contentToCheck);
  const languageHint = detectLanguage(contentToCheck);

  // Load appropriate fonts based on content language
  let helvetica: PDFFont;
  let helveticaBold: PDFFont;
  let helveticaOblique: PDFFont;
  let helveticaBoldOblique: PDFFont;

  if (needsUnicode) {
    // Load Unicode fonts (Noto Sans KR) for CJK content
    const fonts = await loadUnicodeFontFamily(pdfDoc, languageHint);
    helvetica = fonts.regular;
    helveticaBold = fonts.bold;
    helveticaOblique = fonts.italic; // CJK fonts typically don't have italic, using regular
    helveticaBoldOblique = fonts.bold; // Use bold as fallback for bold-italic
  } else {
    // Load standard Helvetica fonts for Latin-only content (smaller file size)
    helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
    helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    helveticaOblique = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
    helveticaBoldOblique = await pdfDoc.embedFont(StandardFonts.HelveticaBoldOblique);
  }

  // Set PDF metadata (use translated title if provided)
  const displayTitle = translations.title || params.title || 'Formatted Legal Report';
  const sanitizedTitle = sanitizeForPdf(displayTitle, needsUnicode);
  pdfDoc.setTitle(sanitizedTitle);
  pdfDoc.setAuthor(organizationName);
  pdfDoc.setSubject('Formatted Legal Research Report');
  pdfDoc.setKeywords(['immigration', 'legal research', 'formatted report']);
  pdfDoc.setProducer(organizationName);
  pdfDoc.setCreator('prest0');
  pdfDoc.setCreationDate(createdAt);
  pdfDoc.setModificationDate(createdAt);

  const allPages: ReturnType<typeof pdfDoc.addPage>[] = [];
  let lastContentPageIndex = 0; // Track last page that received content

  // State for current page and cursor
  let page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  allPages.push(page);
  let cursorY = PAGE_HEIGHT - MARGIN_TOP;

  // Helper functions
  const addNewPage = (): void => {
    page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    allPages.push(page);
    cursorY = PAGE_HEIGHT - MARGIN_TOP;
    // Every fifth page: often enough to read as motion, rare enough that
    // repainting does not itself slow a 200-page build.
    if (allPages.length % 5 === 0) {
      try {
        params.onProgress?.({ pages: allPages.length });
      } catch { /* a progress paint must never break the build */ }
    }
  };

  // Reserve space for footer (FOOTER_Y + 20pt buffer) to prevent content bleeding into footer area
  const CONTENT_BOTTOM_LIMIT = PAGE_NUM_Y + 30; // 48 + 30 = 78pt from bottom
  
  const ensureSpace = (needed: number): void => {
    if (cursorY - needed < CONTENT_BOTTOM_LIMIT) {
      addNewPage();
    }
  };

  const drawText = (
    text: string,
    x: number,
    y: number,
    font: typeof helvetica,
    size: number,
    color = rgb(0, 0, 0)
  ): void => {
    safeDrawText(page, text, { x, y, size, font, color });
  };

  const drawWrappedText = (
    text: string,
    font: typeof helvetica,
    size: number,
    indent: number,
    lineHeight: number,
    extraSpacing: number,
    color = rgb(0, 0, 0)
  ): void => {
    const maxWidth = CONTENT_WIDTH - indent;
    const wrapped = wrapText(text, font, size, maxWidth);
    for (const line of wrapped) {
      ensureSpace(lineHeight);
      drawText(line, MARGIN_LEFT + indent, cursorY, font, size, color);
      cursorY -= lineHeight;
      lastContentPageIndex = allPages.length - 1; // Track that this page has content
    }
    cursorY -= extraSpacing;
  };

  // ============================================================================
  // PARSE CONTENT AND EXTRACT IMPORTANT NOTICE
  // ============================================================================
  // Phase 1: Split content into body and references to preserve URLs in references section
  // Phase 2: Normalize and parse clean text for structure
  const { body, references } = extractAndPreserveReferences(params.content);
  const normalizedBody = normalizeContent(body, needsUnicode);
  let normalizedReferences = '';
  if (references) {
    const refsWithVisibleUrls = convertReferencesToPlaintext(references);
    normalizedReferences = normalizeContent(refsWithVisibleUrls, needsUnicode);
  }
  const normalizedContent = normalizeSourcePageMarkers(
    normalizedBody + (normalizedReferences ? '\n\n' + normalizedReferences : ''),
  );
  const allBlocks = parseBlocks(normalizedContent);
  const { noticeBlocks, remainingBlocks } = extractImportantNotice(allBlocks);

  // ============================================================================
  // TITLE PAGE
  // ============================================================================
  const includeCoverPage = params.includeCoverPage !== false;

  // ── Density for a document the user supplied (2026-08-14) ────────────────
  // Measured on the delivered 45-page report: 1,281 chars per page against the
  // source's 2,600 — we were using half the paper for the same words, so a
  // 45-page source came back as 88 pages and every page reference in the text
  // pointed somewhere else.
  //
  // The waste is SPACING, not type size: 14pt leading on 10pt text is 1.4x,
  // and 12pt between every paragraph costs about a line each time. Tightening
  // both to normal document values recovers roughly a quarter of the page
  // without shrinking a single glyph — legibility is the thing a client
  // actually judges, so it is the last thing to trade.
  //
  // Applied ONLY to user-supplied documents (the same signal as the cover page
  // and the footer numbering); research reports we author keep their airier
  // house style.
  const denseLayout = !includeCoverPage;
  const bodyLineHeight = denseLayout ? 11.5 : LINE_HEIGHT_BODY;
  const paragraphSpacing = denseLayout ? 6 : PARAGRAPH_SPACING;
  const beforeSectionHeader = denseLayout ? 10 : BEFORE_SECTION_HEADER;
  if (includeCoverPage) {
  cursorY = PAGE_HEIGHT - MARGIN_TOP - 100;

  // Document title (uses translation if provided)
  const titleLines = wrapText(sanitizedTitle, helveticaBold, FONT_SIZE_TITLE, CONTENT_WIDTH);
  for (const line of titleLines) {
    const titleWidth = helveticaBold.widthOfTextAtSize(line, FONT_SIZE_TITLE);
    drawText(line, (PAGE_WIDTH - titleWidth) / 2, cursorY, helveticaBold, FONT_SIZE_TITLE);
    cursorY -= 24;
  }

  cursorY -= 20;

  // Date (uses translation if provided)
  const dateWidth = helvetica.widthOfTextAtSize(dateStr, FONT_SIZE_BODY);
  drawText(dateStr, (PAGE_WIDTH - dateWidth) / 2, cursorY, helvetica, FONT_SIZE_BODY, rgb(0.4, 0.4, 0.4));
  cursorY -= 30;

  // ============================================================================
  // IMPORTANT NOTICE SECTION (Extracted from AI Content - Cover Page)
  // ============================================================================
  if (noticeBlocks.length > 0) {
    const boxX = MARGIN_LEFT + 20;
    const boxWidth = CONTENT_WIDTH - 40;
    const boxStartY = cursorY;

    // Render each notice block
    for (const block of noticeBlocks) {
      switch (block.kind) {
        case 'section':
          // Important Notice header - centered and bold
          const headerWidth = helveticaBold.widthOfTextAtSize(block.text, 12);
          drawText(block.text, (PAGE_WIDTH - headerWidth) / 2, cursorY, helveticaBold, 12);
          cursorY -= 18;
          break;
        case 'paragraph':
          // Body text - wrapped within the box
          const paraLines = wrapText(block.text, helvetica, 9, boxWidth - 20);
          for (const pLine of paraLines) {
            drawText(pLine, boxX + 10, cursorY, helvetica, 9);
            cursorY -= 12;
          }
          cursorY -= 4;
          break;
        case 'bullet':
          // Bullet items - indented
          drawText(`*  ${block.text}`, boxX + 20, cursorY, helvetica, 8);
          cursorY -= 10;
          break;
        case 'subsection':
          // "Included:" or "NOT Included:" labels - bold
          cursorY -= 4;
          drawText(block.text, boxX + 10, cursorY, helveticaBold, 9);
          cursorY -= 12;
          break;
        default:
          // Any other block type - wrapped text
          const defaultLines = wrapText(block.text, helvetica, 9, boxWidth - 20);
          for (const dLine of defaultLines) {
            drawText(dLine, boxX + 10, cursorY, helvetica, 9);
            cursorY -= 12;
          }
      }
    }

    cursorY -= 10;

    // Draw box border around the notice
    const boxEndY = cursorY;
    const boxHeight = boxStartY - boxEndY + 15;
    page.drawRectangle({
      x: boxX,
      y: boxEndY - 5,
      width: boxWidth,
      height: boxHeight,
      borderColor: rgb(0.7, 0.7, 0.7),
      borderWidth: 1,
    });

    cursorY -= 20;
  } else {
    // No notice found - add spacing before copyright
    cursorY -= 50;
  }

  // Copyright notice on title page (uses translation if provided)
  const copyrightLines = wrapText(copyrightNotice, helveticaOblique, FONT_SIZE_FOOTER, CONTENT_WIDTH - 40);
  for (const line of copyrightLines) {
    const lineWidth = helveticaOblique.widthOfTextAtSize(line, FONT_SIZE_FOOTER);
    drawText(line, (PAGE_WIDTH - lineWidth) / 2, cursorY, helveticaOblique, FONT_SIZE_FOOTER, rgb(0.5, 0.5, 0.5));
    cursorY -= 12;
  }
  lastContentPageIndex = 0; // Title page has content
  }

  // ============================================================================
  // CONTENT PAGES (using remaining blocks after notice extraction)
  // ============================================================================
  // Only break to a fresh page when a cover page was actually drawn; otherwise
  // the document starts on page 1 as the source does.
  if (includeCoverPage) addNewPage();

  // Use remainingBlocks - Important Notice already extracted for cover page
  /** True once anything has been drawn on the current page. */
  let pageHasContent = false;

  // Figures from the source, grouped by the page they appeared on.
  const figuresByPage = new Map<number, NonNullable<BuildFormattedPdfParams['sourceImages']>>();
  for (const img of params.sourceImages || []) {
    const bucket = figuresByPage.get(img.pageNumber) || [];
    bucket.push(img);
    figuresByPage.set(img.pageNumber, bucket);
  }
  // Within each page, figures are placed in flow order.
  for (const bucket of Array.from(figuresByPage.values())) {
    bucket.sort((a, b) => (a.afterCharRatio ?? 1) - (b.afterCharRatio ?? 1));
  }
  /** Source page currently being rendered; the first page break moves us to 2. */
  let currentSourcePage = 1;

  // How much text each SOURCE page carries, so a figure recorded at 50% of its
  // page can be drawn after 50% of that page's characters. Counted from the
  // blocks themselves, so it follows the translated text rather than the
  // source's own length.
  const charsBySourcePage = new Map<number, number>();
  {
    let page = 1;
    for (const block of remainingBlocks) {
      if (block.kind === 'pagebreak') {
        page = block.sourcePageNumber ?? page + 1;
        continue;
      }
      charsBySourcePage.set(page, (charsBySourcePage.get(page) || 0) + block.text.length);
    }
  }
  /** Characters of the CURRENT source page already drawn. */
  let charsDrawnOnSourcePage = 0;
  const drawnFigures = new Set<string>();

  /** Draw the source page's figures, scaled to the text column. */
  const drawFiguresForPage = async (sourcePage: number, upToRatio = 1): Promise<void> => {
    const figures = figuresByPage.get(sourcePage);
    if (!figures || figures.length === 0) return;
    for (const figure of figures) {
      const key = `${sourcePage}:${figure.base64.length}:${figure.afterCharRatio ?? 1}`;
      if (drawnFigures.has(key)) continue;
      // Figures with no recorded position keep the old end-of-page behaviour.
      if ((figure.afterCharRatio ?? 1) > upToRatio) continue;
      drawnFigures.add(key);
      try {
        const embedded = await pdfDoc.embedPng(`data:image/png;base64,${figure.base64}`);
        const scale = Math.min(1, CONTENT_WIDTH / embedded.width);
        const drawWidth = embedded.width * scale;
        const drawHeight = embedded.height * scale;
        // A figure taller than the text column gets its own page rather than
        // being clipped at the footer.
        ensureSpace(drawHeight + 12);
        page.drawImage(embedded, {
          x: MARGIN_LEFT + (CONTENT_WIDTH - drawWidth) / 2,
          y: cursorY - drawHeight,
          width: drawWidth,
          height: drawHeight,
        });
        cursorY -= drawHeight + 12;
        pageHasContent = true;
        lastContentPageIndex = allPages.length - 1;
      } catch (figureErr) {
        // One unembeddable figure must not cost the document.
        console.warn('[FormattedPdf] Skipped a source figure:', figureErr);
      }
    }
  };

  for (const block of remainingBlocks) {
    if (block.kind !== 'pagebreak') {
      pageHasContent = true;
      // Any figure whose place in this page's flow has been reached is drawn
      // BEFORE the block that follows it, which is where it sat in the source.
      const pageChars = charsBySourcePage.get(currentSourcePage) || 0;
      if (pageChars > 0) {
        await drawFiguresForPage(currentSourcePage, charsDrawnOnSourcePage / pageChars);
      }
      charsDrawnOnSourcePage += block.text.length;
    }
    switch (block.kind) {
      // A page break carried over from the SOURCE document. Starting a new page
      // here is what keeps the deliverable's pagination tracking the original
      // (a 45-page source used to reflow to 25). Skipped when the current page
      // is still empty, so a marker at the very top cannot emit a blank page.
      case 'pagebreak':
        // Figures belong to the page we are LEAVING, so place them before the
        // break — that is where they sat in the source.
        //
        // Only when that page actually HAS content: our own extractor emits
        // "--- Page 1 ---" as the very first line, and drawing on that marker
        // would put page 1's figures above page 1's text and then draw them a
        // second time at the real end of the page.
        if (pageHasContent) await drawFiguresForPage(currentSourcePage);
        currentSourcePage = block.sourcePageNumber ?? currentSourcePage + 1;
        charsDrawnOnSourcePage = 0;
        if (pageHasContent) {
          addNewPage();
          pageHasContent = false;
        }
        break;

      case 'title':
        cursorY -= beforeSectionHeader;
        ensureSpace(30);
        drawWrappedText(block.text.toUpperCase(), helveticaBold, FONT_SIZE_TITLE, 0, 22, AFTER_SECTION_HEADER);
        break;

      case 'part':
        cursorY -= beforeSectionHeader;
        ensureSpace(24);
        drawWrappedText(block.text, helveticaBold, FONT_SIZE_PART_HEADER, 0, 18, AFTER_SECTION_HEADER);
        break;

      case 'section':
        cursorY -= beforeSectionHeader;
        ensureSpace(20);
        drawWrappedText(block.text, helveticaBold, FONT_SIZE_SECTION_HEADER, 0, 16, AFTER_SECTION_HEADER);
        break;

      case 'subsection':
        cursorY -= 10;
        ensureSpace(18);
        drawWrappedText(block.text, helveticaBold, FONT_SIZE_BODY, 0, bodyLineHeight, 4);
        break;

      case 'bullet':
        ensureSpace(bodyLineHeight * 2);
        drawWrappedText(`*  ${block.text}`, helvetica, FONT_SIZE_BODY, BULLET_INDENT, bodyLineHeight, BULLET_SPACING);
        break;

      case 'numbered':
        ensureSpace(bodyLineHeight * 2);
        drawWrappedText(block.text, helvetica, FONT_SIZE_BODY, BULLET_INDENT, bodyLineHeight, BULLET_SPACING);
        break;

      case 'callout':
        cursorY -= 6;
        ensureSpace(bodyLineHeight * 3);
        drawWrappedText(block.text, helveticaBoldOblique, FONT_SIZE_BODY, CALLOUT_INDENT, bodyLineHeight, BULLET_SPACING);
        cursorY -= 6;
        break;

      case 'table':
        if (block.tableRows && block.tableRows.length > 0) {
          cursorY -= 8;
          const numCols = Math.max(...block.tableRows.map(r => r.length));
          const cellPadding = 4;

          // ── Proportional columns + wrapping (2026-08-14) ─────────────────
          // Columns used to be CONTENT_WIDTH / numCols with any overflowing
          // cell chopped and given an ellipsis. On the 45-page report that
          // shipped "Division A - Prese..." and "Division C - Bever..." to the
          // client: SILENT DATA LOSS in a financial deliverable, and
          // unrecoverable from the delivered file.
          //
          // Widths now follow the widest cell in each column (bounded so one
          // long cell cannot starve the rest), and anything still too wide
          // WRAPS onto extra lines. Losing a row's height is recoverable;
          // losing its text is not.
          const rawWidths = Array.from({ length: numCols }, (_, colIdx) =>
            Math.max(
              ...block.tableRows!.map((r) =>
                helvetica.widthOfTextAtSize(r[colIdx] || '', FONT_SIZE_BODY),
              ),
              1,
            ) + cellPadding * 2,
          );
          const rawTotal = rawWidths.reduce((a, b) => a + b, 0);
          const minWidth = CONTENT_WIDTH / (numCols * 2.5);
          let colWidths = rawWidths.map((w) =>
            Math.max(minWidth, (w / rawTotal) * CONTENT_WIDTH),
          );
          const scale = CONTENT_WIDTH / colWidths.reduce((a, b) => a + b, 0);
          colWidths = colWidths.map((w) => w * scale);
          const colX: number[] = [];
          for (let c = 0, x = MARGIN_LEFT; c < numCols; c++) {
            colX.push(x);
            x += colWidths[c];
          }

          // Wrap every cell up front so row heights follow the tallest cell.
          const wrappedRows = block.tableRows.map((row, rIdx) =>
            Array.from({ length: numCols }, (_, colIdx) =>
              wrapText(
                row[colIdx] || '',
                rIdx === 0 ? helveticaBold : helvetica,
                FONT_SIZE_BODY,
                colWidths[colIdx] - cellPadding * 2,
              ),
            ),
          );
          const rowHeights = wrappedRows.map(
            (cells) =>
              Math.max(1, ...cells.map((lines) => lines.length)) * bodyLineHeight +
              cellPadding * 2,
          );

          ensureSpace(rowHeights.reduce((a, b) => a + b, 0) + 4);

          // Draw each row
          for (let rowIdx = 0; rowIdx < block.tableRows.length; rowIdx++) {
            const row = block.tableRows[rowIdx];
            const isHeader = rowIdx === 0;
            const rowHeight = rowHeights[rowIdx];
            // A long table must not run off the bottom of the page.
            ensureSpace(rowHeight);
            const rowY = cursorY;

            // Draw cells
            for (let colIdx = 0; colIdx < numCols; colIdx++) {
              const cellText = colIdx < row.length ? row[colIdx] : '';
              const colWidth = colWidths[colIdx];
              const cellX = colX[colIdx];
              const font = isHeader ? helveticaBold : helvetica;

              // Draw cell background for header
              if (isHeader) {
                page.drawRectangle({
                  x: cellX,
                  y: rowY - rowHeight + cellPadding,
                  width: colWidth,
                  height: rowHeight,
                  color: rgb(0.95, 0.95, 0.95),
                });
              }

              // Draw cell border
              page.drawRectangle({
                x: cellX,
                y: rowY - rowHeight + cellPadding,
                width: colWidth,
                height: rowHeight,
                borderColor: rgb(0.8, 0.8, 0.8),
                borderWidth: 0.5,
              });

              // Draw cell text, WRAPPED — never truncated. An ellipsis here is
              // unrecoverable data loss in the client's deliverable.
              const lines = wrapText(cellText, font, FONT_SIZE_BODY, colWidth - cellPadding * 2);
              lines.forEach((line, lineIdx) => {
                drawText(
                  line,
                  cellX + cellPadding,
                  rowY - cellPadding - bodyLineHeight * (lineIdx + 1) + 4,
                  font,
                  FONT_SIZE_BODY,
                );
              });
            }

            cursorY -= rowHeight;
          }

          cursorY -= 8;
        }
        break;

      case 'paragraph':
      default:
        ensureSpace(bodyLineHeight * 2);
        drawWrappedText(block.text, helvetica, FONT_SIZE_BODY, 0, bodyLineHeight, paragraphSpacing);
        break;
    }
  }

  // The last source page has no trailing marker, so flush its figures here.
  await drawFiguresForPage(currentSourcePage);

  // ============================================================================
  // REMOVE TRAILING EMPTY PAGES
  // ============================================================================
  // Pages created by ensureSpace() but never written to should be removed
  while (allPages.length - 1 > lastContentPageIndex && allPages.length > 1) {
    pdfDoc.removePage(allPages.length - 1);
    allPages.pop();
  }

  // ============================================================================
  // ADD PAGE NUMBERS AND FOOTERS TO ALL PAGES
  // ============================================================================
  for (let idx = 0; idx < allPages.length; idx++) {
    const pg = allPages[idx];
    const pageNum = idx + 1;

    // Page number (bottom center) - uses translation format if provided.
    // Suppressed for a document the user supplied: it already carries its own
    // running header and footer ("CONFIDENTIAL / INTERNAL USE - Page 1 of 45"),
    // so ours was a SECOND page number on every page, disagreeing with the
    // first. Same signal as the cover page — this is their document, and it
    // arrives with its own furniture.
    if (includeCoverPage) {
      const pageNumText = pageNumberFormat.replace('{current}', String(pageNum));
      const pageNumWidth = helvetica.widthOfTextAtSize(pageNumText, FONT_SIZE_PAGE_NUM);
      safeDrawText(pg, pageNumText, {
        x: (PAGE_WIDTH - pageNumWidth) / 2,
        y: PAGE_NUM_Y,
        size: FONT_SIZE_PAGE_NUM,
        font: helvetica,
        color: rgb(0.4, 0.4, 0.4),
      });
    }

    // Footer copyright (on every page) - uses translation if provided
    const footerWidth = helvetica.widthOfTextAtSize(copyrightShort, FONT_SIZE_FOOTER);
    safeDrawText(pg, copyrightShort, {
      x: (PAGE_WIDTH - footerWidth) / 2,
      y: FOOTER_Y,
      size: FONT_SIZE_FOOTER,
      font: helvetica,
      color: rgb(0.5, 0.5, 0.5),
    });
  }

  // ============================================================================
  // SAVE AND RETURN
  // ============================================================================
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });

  // Generate filename
  const slugify = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50);
  };
  const subjectSlug = slugify(params.title || 'formatted-report');
  const dateSlug = createdAt.toISOString().slice(0, 10);
  const filename = `${subjectSlug}-formatted-${dateSlug}.pdf`;

  return new File([blob], filename, { type: 'application/pdf', lastModified: createdAt.getTime() });
}

